import React, { useEffect } from 'react';
import { Target, Eye, Award, CheckCircle, Briefcase, Building2, TrendingUp, Users, ShieldCheck, Lightbulb } from 'lucide-react';
import './AboutPage.css';
import Navbar from '../LandingPage/Navbar';
import Footer from '../LandingPage/Footer';

const AboutPage = () => {
  // Ensure page starts at top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
    <Navbar/>

    <div className="about-page">
      
      {/* --- 1. Page Header (Parallax) --- */}
      <header className="page-header">
        <div className="header-overlay"></div>
        <div className="container header-content">
          <span className="premium-badge">Established Excellence</span>
          <h1 className="page-title">About Property Galleria</h1>
          <div className="gold-divider"></div>
          <p className="page-subtitle">Your Trusted Partner in Realty & Financial Advisory</p>
        </div>
      </header>

      {/* --- 2. Corporate Profile & Identity --- */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid-2-col">
            <div className="text-block">
              <h2 className="section-heading">Company <span className="highlight">Profile</span></h2>
              <p className="body-text">
                Property Galleria LLP brings together a strong blend of financial expertise, real estate investment knowledge, customer service, and legal assurance. Our goal is to simplify property decisions and deliver value-driven solutions tailored to every client’s needs.
              </p>
              <p className="body-text">
                We are a client-friendly realty advisory and management company dedicated to making property and investment decisions simple, transparent, and profitable. With decades of expertise in financial services, insurance, and real estate, we guide individuals, families, and businesses in creating wealth and building a secure future.
              </p>
            </div>
            {/* Vision & Mission Stack */}
            <div className="vm-stack">
              <div className="vm-card">
                <div className="icon-box"><Eye size={24} /></div>
                <div>
                  <h4>Our Vision</h4>
                  <p>To be the most trusted and client-focused realty advisory, empowering individuals with solutions that simplify real estate decisions.</p>
                </div>
              </div>
              <div className="vm-card dark">
                <div className="icon-box"><Target size={24} /></div>
                <div>
                  <h4>Our Mission</h4>
                  <p>To guide 1 million clients through every step of their real estate journey, offering reliable advice and tailored solutions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 3. Leadership Team (Premium Cards) --- */}
      <section className="section-padding bg-dark">
        <div className="container">
          <div className="section-center-header">
            <span className="subtitle-teal">The Visionaries</span>
            <h2 className="section-heading text-dark">Our Leadership Team</h2>
            <div className="gold-divider center"></div>
          </div>

          <div className="leadership-grid-3">
            {/* Leader 1 */}
            <div className="leader-card">
              <div className="leader-img-placeholder">PP</div>
              <div className="leader-body">
                <h3>Mr. Pradeep Patel</h3>
                <span className="designation">Managing Partner</span>
                <p className="leader-bio">
                  With over 20 years of experience, holding leadership roles at Bajaj Capital, SBILIFE, and PNB METLIFE. His command over mutual funds and property advisory drives strategic solutions.
                </p>
              </div>
            </div>

            {/* Leader 2 */}
            <div className="leader-card">
              <div className="leader-img-placeholder">MR</div>
              <div className="leader-body">
                <h3>Mr. Muddam Ravinder Reddy</h3>
                <span className="designation">Managing Partner</span>
                <p className="leader-bio">
                  A seasoned professional with 20+ years in life insurance advisory (ING Vysya, SBI Life). His operational expertise ensures seamless and client‑oriented service.
                </p>
              </div>
            </div>

            {/* Leader 3 */}
            <div className="leader-card">
              <div className="leader-img-placeholder">SP</div>
              <div className="leader-body">
                <h3>Ms. Shailaja Patel</h3>
                <span className="designation">Independent Director</span>
                <p className="leader-bio">
                  A practicing advocate with 8+ years in civil litigation and project advisory. She ensures regulatory compliance, transparency, and trust in all client engagements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

 {/* --- 4. Core Values (Premium Dark Grid) --- */}
      <section className="section-padding bg-velvet">
        <div className="container">
          <div className="values-header">
            <h2 className="section-heading text-white">Our Core <span className="highlight-gold">Values</span></h2>
            <p className="values-intro">The principles that guide our legacy.</p>
          </div>
          
          <div className="values-grid-premium">
            {[
              { title: "Client‑Centricity", desc: "We prioritize client needs and deliver solutions that exceed expectations.", icon: <Users /> },
              { title: "Integrity", desc: "Transparency and honesty guide every interaction.", icon: <ShieldCheck /> },
              { title: "Excellence", desc: "Expert advice, insights, and flawless execution.", icon: <Award /> },
              { title: "Innovation", desc: "Adopting new technologies and market trends.", icon: <Lightbulb /> },
              { title: "Accountability", desc: "We take responsibility for every commitment.", icon: <CheckCircle /> },
              { title: "Evolution", desc: "Staying updated and continuously learning.", icon: <TrendingUp /> },
              { title: "Collaboration", desc: "Teamwork with clients and partners.", icon: <Briefcase /> },
              { title: "Sustainability", desc: "Environment‑friendly realty practices.", icon: <Building2 /> },
              { title: "Respect", desc: "Valuing people and diverse perspectives.", icon: <Users /> },
              { title: "Partnership", desc: "Relationships beyond transactions.", icon: <Target /> },
            ].map((val, idx) => (
              <div className="value-card-premium" key={idx}>
                <div className="v-icon-box">{val.icon}</div>
                <h5 className="v-title">{val.title}</h5>
                <p className="v-desc">{val.desc}</p>
                <div className="v-border-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* --- 5. The Transformation (Problems & Solutions) --- */}
      <section className="section-padding transformation-section">
        <div className="container">
          <div className="transformation-wrapper">
            
            {/* Left: The Challenge (Problems) */}
            <div className="challenge-card">
              <h3 className="trans-heading text-dark">PROBLEMS WE  <span className="text-teal">SOLVE</span></h3>
              <p className="trans-sub">Common hurdles in the real estate journey:</p>
              <ul className="challenge-list">
                {[
                  "Confusion in selecting the right property",
                  "Lack of reliable advisory & legal support",
                  "Complex documentation issues",
                  "Unclear investment strategies",
                  "Hidden costs & mistrust",
                  "Poor after‑sales property management"
                ].map((item, i) => (
                  <li key={i}>
                    <span className="bullet-x">✕</span> {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: The Solution (Specializations) - Floats on top */}
            <div className="solution-card">
              <div className="glow-circle"></div>
              <h3 className="trans-heading text-white">The Galleria <span className="text-gold">SPECIALIZATIONS</span></h3>
              <p className="trans-sub text-white-50">Our specialized expertise simplifies success:</p>
              <ul className="solution-list">
                {[
                  "Real estate investment advisory",
                  "Legal vetting & documentation support",
                  "Property resale & portfolio management",
                  "Financial planning for realty investments",
                  "Transparent, client‑first consultations"
                ].map((item, i) => (
                  <li key={i}>
                    <CheckCircle className="bullet-check" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

    </div>
    <Footer/>
        </>
  );
};

export default AboutPage;